import React, { useState, useEffect } from "react";
import InputMask from "react-input-mask";
import Navbar from "../common/Navbar";
import LogoComponent from "../common/LogoComponent";
import customerServices from "../../services/CustomerService";
import ContactUs from "../common/ContactUs";
import Footer from "../common/Footer";

const RequestCallPage = () => {
  useEffect(() => {
    document.title = "Request Call";
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateField = (name, value) => {
    const newErrors = {};
    if (name === "name" && !value.trim()) {
      newErrors.name = "Name is required";
    }
    if (name === "email") {
      if (!value.trim()) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(value)) {
        newErrors.email = "Invalid email format";
      }
    }
    if (name === "phone") {
      if (!value.trim()) {
        newErrors.phone = "Phone number is required";
      } else if (!/^\(\d{3}\)\s\d{3}-\d{4}$/.test(value)) {
        newErrors.phone = "Invalid phone number format (e.g., (123) 456-7890)";
      }
    }
    // Message field is not required, so no validation for it
    // if (!formData.message.trim()) {
    //   newErrors.message = "Message is required";
    // }
    return newErrors;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\(\d{3}\)\s\d{3}-\d{4}$/.test(formData.phone)) {
      newErrors.phone = "Invalid phone number format (e.g., (123) 456-7890)";
    }
    // Message field is not required
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const fieldErrors = validateField(name, value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      ...fieldErrors,
    }));
  };

  const handleRequestCall = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await customerServices.RequestCall(formData);

      if (response?.resultData === true) {
        setIsSubmitted(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          message: "",
        });
        setErrors({});
      } else {
        console.error("Failed to send request");
      }
    } catch (error) {
      console.error("Error while sending request:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ fontFamily: "Arial, sans-serif" }}>
      <Navbar />
      <div
        className="d-flex flex-column align-items-center justify-content-center text-center p-3 py-5"
        style={{ backgroundColor: "#f5f6f7", minHeight: "100vh" }}
      >
        {isSubmitted ? (
          <div className="mt-5">
            <LogoComponent inverted={true} size={60} />
            <h1 className="fw-bold mb-3 display-2" id="main-font">
              Thank You!
            </h1>
            <p className="lead display-5" id="main-font">
              Our team has received your request. We will contact you soon via
              phone or email.
            </p>
          </div>
        ) : (
          <div style={{ maxWidth: "600px", width: "100%" }}>
            <div className="heading d-flex justify-content-center align-items-center mb-4">
              <LogoComponent inverted={true} size={70} />
              <h1 className="fw-bold display-3 mb-0" id="main-font">
                REQUEST A CALL
              </h1>
            </div>
            <p className="lead mb-4 display-4" id="main-font">
              Leave your details and we will get back to you shortly.
            </p>

            <div className="mb-3 text-start">
              <label htmlFor="name" className="form-label">
                Name
              </label>
              <input
                type="text"
                className={`form-control ${errors.name ? "is-invalid" : ""}`}
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              {errors.name && (
                <div className="invalid-feedback">{errors.name}</div>
              )}
            </div>

            <div className="mb-3 text-start">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                type="email"
                className={`form-control ${errors.email ? "is-invalid" : ""}`}
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              {errors.email && (
                <div className="invalid-feedback">{errors.email}</div>
              )}
            </div>

            <div className="mb-3 text-start">
              <label htmlFor="phone" className="form-label">
                Phone Number
              </label>
              <InputMask
                mask="(999) 999-9999"
                type="text"
                name="phone"
                className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                value={formData.phone || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              {errors.phone && (
                <div className="invalid-feedback">{errors.phone}</div>
              )}
            </div>

            <div className="mb-3 text-start">
              <label htmlFor="message" className="form-label">
                Message
              </label>
              <textarea
                className={`form-control ${errors.message ? "is-invalid" : ""}`}
                id="message"
                name="message"
                rows="4"
                value={formData.message}
                onChange={handleChange}
                onBlur={handleBlur}
              ></textarea>
              {errors.message && (
                <div className="invalid-feedback">{errors.message}</div>
              )}
            </div>

            <button
              type="button"
              className="btn btn-outline-dark w-100"
              onClick={handleRequestCall}
              id="main-font"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        )}
      </div>
      <ContactUs />
      <Footer />
    </div>
  );
};

export default RequestCallPage;
